import type {
  CreateAccountFormData,
  CreateAccountFormErrors
} from "./accountCreation";
import {
  ACCOUNT_ID_MAX_LENGTH,
  ACCOUNT_ID_MIN_LENGTH,
  ADDRESS_MAX_LENGTH,
  CITY_MAX_LENGTH,
  COMPANY_NAME_MAX_LENGTH,
  COUNTRY_LENGTH,
  STATE_LENGTH,
  ValidationConstraints
} from "./accountCreation";

const LETTER_ONLY_PATTERN = /^[A-Za-z]+$/;
const ACCOUNT_ID_PATTERN = /^[a-z0-9]+$/;

// Validation Helper Functions
export const validateCompanyName = (name: string): string | undefined => {
  if (!name || name.trim().length === 0) {
    return ValidationConstraints.CREATE_ACCOUNT.COMPANY_NAME_REQUIRED;
  }
  if (name.length > COMPANY_NAME_MAX_LENGTH) {
    return ValidationConstraints.CREATE_ACCOUNT.COMPANY_NAME_MAX_LENGTH;
  }
  return undefined;
};

export const validateAddress1 = (address1?: string): string | undefined => {
  if (!address1 || address1.trim().length === 0) {
    return ValidationConstraints.CREATE_ACCOUNT.ADDRESS_REQUIRED;
  }
  if (address1.length > ADDRESS_MAX_LENGTH) {
    return ValidationConstraints.CREATE_ACCOUNT.ADDRESS_MAX_LENGTH;
  }
  return undefined;
};

export const validateAddress2 = (address2?: string): string | undefined => {
  if (!!address2 && address2.length > ADDRESS_MAX_LENGTH) {
    return ValidationConstraints.CREATE_ACCOUNT.ADDRESS_MAX_LENGTH;
  }
  return undefined;
};

export const validateCity = (city?: string): string | undefined => {
  if (!city || city.trim().length === 0) {
    return ValidationConstraints.CREATE_ACCOUNT.CITY_REQUIRED;
  }
  if (city.length > CITY_MAX_LENGTH) {
    return ValidationConstraints.CREATE_ACCOUNT.CITY_MAX_LENGTH;
  }
  return undefined;
};

export const validateZipCode = (zipCode?: string): string | undefined => {
  if (!zipCode || zipCode.trim().length === 0) {
    return ValidationConstraints.CREATE_ACCOUNT.ZIP_CODE_REQUIRED;
  }

  const zipCodePattern = /^\d{5}(-\d{4})?$/;
  if (!zipCodePattern.test(zipCode)) {
    return ValidationConstraints.CREATE_ACCOUNT.ZIP_CODE_FORMAT;
  }
  return undefined;
};

export const validateCountry = (country?: string): string | undefined => {
  if (!country || country.trim().length === 0) {
    return ValidationConstraints.CREATE_ACCOUNT.COUNTRY_REQUIRED;
  }

  if (country.length !== COUNTRY_LENGTH || !LETTER_ONLY_PATTERN.test(country)) {
    return ValidationConstraints.CREATE_ACCOUNT.COUNTRY_LENGTH;
  }
  return undefined;
};

export const validateState = (state?: string): string | undefined => {
  if (!state || state.trim().length === 0) {
    return ValidationConstraints.CREATE_ACCOUNT.STATE_REQUIRED;
  }

  if (state.length !== STATE_LENGTH || !LETTER_ONLY_PATTERN.test(state)) {
    return ValidationConstraints.CREATE_ACCOUNT.STATE_LENGTH;
  }
  return undefined;
};

export const validateRequestedServiceAccountId = (
  id?: string
): string | undefined => {
  if (!id || id.trim().length === 0) {
    return undefined; // optional field
  }
  if (!ACCOUNT_ID_PATTERN.test(id)) {
    return ValidationConstraints.CREATE_ACCOUNT.ACCOUNT_ID_FORMAT;
  }
  if (id.length < ACCOUNT_ID_MIN_LENGTH || id.length > ACCOUNT_ID_MAX_LENGTH) {
    return ValidationConstraints.CREATE_ACCOUNT.ACCOUNT_ID_LENGTH;
  }
  return undefined;
};

// Validation function for complete form
export const validateCreateAccountForm = (
  formData: CreateAccountFormData
): CreateAccountFormErrors => {
  const errors: CreateAccountFormErrors = {};

  const companyNameError = validateCompanyName(formData.companyName);
  if (companyNameError) {
    errors.companyName = companyNameError;
  }

  const accountIdError = validateRequestedServiceAccountId(
    formData.requestedServiceAccountId
  );
  if (accountIdError) {
    errors.requestedServiceAccountId = accountIdError;
  }

  if (!formData.accountType) {
    errors.accountType =
      ValidationConstraints.CREATE_ACCOUNT.ACCOUNT_TYPE_REQUIRED;
  }

  if (!formData.verticalType) {
    errors.verticalType =
      ValidationConstraints.CREATE_ACCOUNT.VERTICAL_TYPE_REQUIRED;
  }

  const address1Error = validateAddress1(formData.address1);
  if (address1Error) {
    errors.address1 = address1Error;
  }

  const address2Error = validateAddress2(formData.address2);
  if (address2Error) {
    errors.address2 = address2Error;
  }

  const cityError = validateCity(formData.city);
  if (cityError) {
    errors.city = cityError;
  }

  const stateError = validateState(formData.state);
  if (stateError) {
    errors.state = stateError;
  }

  const zipCodeError = validateZipCode(formData.zipCode);
  if (zipCodeError) {
    errors.zipCode = zipCodeError;
  }

  const countryError = validateCountry(formData.country);
  if (countryError) {
    errors.country = countryError;
  }

  return errors;
};
